import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Upload,
  Image as ImageIcon,
  Check,
  Palette,
  Type,
  Layout,
  Save,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminBrandKit() {
  const [brandkits, setBrandkits] = useState([]);
  const [selectedBrandkit, setSelectedBrandkit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [tagline, setTagline] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#3A7BFF');
  const [secondaryColor, setSecondaryColor] = useState('#39E6C8');
  const [fontFamily, setFontFamily] = useState('Inter');
  const [templateStyle, setTemplateStyle] = useState('top_right');
  const [symbolUrl, setSymbolUrl] = useState('');
  const [wordmarkUrl, setWordmarkUrl] = useState('');

  useEffect(() => {
    loadBrandkits();
  }, []);

  const loadBrandkits = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/brandkits/app`, {
        credentials: 'include'
      });
      const data = await res.json();
      setBrandkits(data);
      
      // Auto-select Consistency App BrandKit
      const consistencyBrandkit = data.find(bk => bk.name === 'Consistency App BrandKit');
      if (consistencyBrandkit) {
        selectBrandkit(consistencyBrandkit);
      }
    } catch (error) {
      console.error('Error loading brandkits:', error);
      toast.error('Failed to load brandkits');
    } finally {
      setLoading(false);
    }
  };

  const selectBrandkit = (brandkit) => {
    setSelectedBrandkit(brandkit);
    setName(brandkit.name || '');
    setTagline(brandkit.tagline || '');
    setPrimaryColor(brandkit.primary_color || '#3A7BFF');
    setSecondaryColor(brandkit.secondary_color || '#39E6C8');
    setFontFamily(brandkit.font_family || 'Inter');
    setTemplateStyle(brandkit.template_style || 'top_right');
    setSymbolUrl(brandkit.logo_symbol_url || '');
    setWordmarkUrl(brandkit.logo_wordmark_url || '');
  };

  const handleSave = async () => {
    if (!selectedBrandkit) return;
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('tagline', tagline);
      formData.append('primary_color', primaryColor);
      formData.append('secondary_color', secondaryColor);
      formData.append('font_family', fontFamily);
      formData.append('template_style', templateStyle);
      
      const res = await fetch(`${API}/brandkits/${selectedBrandkit.id}`, {
        method: 'PUT',
        credentials: 'include',
        body: formData
      });
      
      if (!res.ok) throw new Error('Failed to save');
      
      toast.success('BrandKit saved successfully');
      await loadBrandkits();
    } catch (error) {
      console.error('Error saving brandkit:', error);
      toast.error('Failed to save brandkit');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (type, file) => {
    if (!selectedBrandkit || !file) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const endpoint = type === 'symbol' ? 'upload-symbol' : 'upload-wordmark';
      
      const res = await fetch(`${API}/brandkits/${selectedBrandkit.id}/${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!res.ok) throw new Error('Failed to upload');
      
      const data = await res.json();
      
      if (type === 'symbol') {
        setSymbolUrl(data.logo_symbol_url);
      } else {
        setWordmarkUrl(data.logo_wordmark_url);
      }
      
      toast.success(`${type === 'symbol' ? 'Symbol' : 'Wordmark'} uploaded successfully`);
      await loadBrandkits();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading brandkits...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <Palette className="h-8 w-8 text-pink-500" />
          App BrandKit Management
        </h1>
        <p className="text-[#8A93A6]">
          Manage Consistency App branding assets and styles
        </p>
      </div>

      {/* BrandKit Selector */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
        {brandkits.map((brandkit) => (
          <Card
            key={brandkit.id}
            className={`glass cursor-pointer transition-all ${
              selectedBrandkit?.id === brandkit.id ? 'ring-2 ring-pink-500' : ''
            }`}
            onClick={() => selectBrandkit(brandkit)}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <Sparkles className="h-6 w-6 text-pink-500" />
                {brandkit.is_active && <Badge variant="default">Active</Badge>}
              </div>
              <h3 className="text-lg font-semibold text-white">{brandkit.name}</h3>
              <p className="text-sm text-[#8A93A6] mt-1">{brandkit.tagline || 'No tagline'}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedBrandkit && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column: Assets Upload */}
          <div className="space-y-6">
            {/* Logo Symbol */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5 text-pink-500" />
                  Logo Symbol
                </CardTitle>
                <CardDescription>Upload primary logo symbol (PNG/SVG)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {symbolUrl && (
                  <div className="p-4 bg-[#1A1D25] rounded-lg">
                    <p className="text-xs text-[#8A93A6] mb-2">Current Symbol:</p>
                    <p className="text-sm text-white break-all">{symbolUrl}</p>
                  </div>
                )}
                
                <label className="block">
                  <input
                    type="file"
                    accept="image/png,image/svg+xml"
                    className="hidden"
                    onChange={(e) => handleFileUpload('symbol', e.target.files[0])}
                  />
                  <Button variant="outline" className="w-full gap-2" asChild>
                    <span>
                      <Upload className="h-4 w-4" />
                      Upload Symbol
                    </span>
                  </Button>
                </label>
              </CardContent>
            </Card>

            {/* Logo Wordmark */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Type className="h-5 w-5 text-blue-500" />
                  Logo Wordmark
                </CardTitle>
                <CardDescription>Upload secondary wordmark logo (PNG/SVG)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {wordmarkUrl && (
                  <div className="p-4 bg-[#1A1D25] rounded-lg">
                    <p className="text-xs text-[#8A93A6] mb-2">Current Wordmark:</p>
                    <p className="text-sm text-white break-all">{wordmarkUrl}</p>
                  </div>
                )}
                
                <label className="block">
                  <input
                    type="file"
                    accept="image/png,image/svg+xml"
                    className="hidden"
                    onChange={(e) => handleFileUpload('wordmark', e.target.files[0])}
                  />
                  <Button variant="outline" className="w-full gap-2" asChild>
                    <span>
                      <Upload className="h-4 w-4" />
                      Upload Wordmark
                    </span>
                  </Button>
                </label>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Branding Settings */}
          <div className="space-y-6">
            {/* Basic Info */}
            <Card className="glass">
              <CardHeader>
                <CardTitle>BrandKit Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-[#1A1D25] border-[#2A2D35]"
                  />
                </div>

                <div>
                  <Label>Tagline</Label>
                  <Input
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder="Create Freely"
                    className="bg-[#1A1D25] border-[#2A2D35]"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Colors */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5 text-green-500" />
                  Brand Colors
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Primary Color</Label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="h-10 w-20 rounded border border-[#2A2D35]"
                      />
                      <Input
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        className="bg-[#1A1D25] border-[#2A2D35]"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Secondary Color</Label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        className="h-10 w-20 rounded border border-[#2A2D35]"
                      />
                      <Input
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        className="bg-[#1A1D25] border-[#2A2D35]"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Typography & Template */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layout className="h-5 w-5 text-yellow-500" />
                  Typography & Layout
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Font Family</Label>
                  <Select value={fontFamily} onValueChange={setFontFamily}>
                    <SelectTrigger className="bg-[#1A1D25] border-[#2A2D35]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Inter">Inter</SelectItem>
                      <SelectItem value="Space Grotesk">Space Grotesk</SelectItem>
                      <SelectItem value="Roboto">Roboto</SelectItem>
                      <SelectItem value="Open Sans">Open Sans</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Template Style</Label>
                  <Select value={templateStyle} onValueChange={setTemplateStyle}>
                    <SelectTrigger className="bg-[#1A1D25] border-[#2A2D35]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top_left">Top Left</SelectItem>
                      <SelectItem value="top_right">Top Right</SelectItem>
                      <SelectItem value="bottom_left">Bottom Left</SelectItem>
                      <SelectItem value="bottom_right">Bottom Right</SelectItem>
                      <SelectItem value="minimal">Minimal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full gap-2"
              size="lg"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save BrandKit
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Info Alert */}
      <Alert className="mt-6 border-blue-500/20 bg-blue-500/10">
        <AlertCircle className="h-4 w-4 text-blue-500" />
        <AlertDescription className="text-sm text-[#8A93A6]">
          <strong className="text-white">Note:</strong> App BrandKit is used for all Consistency App branding including landing pages, self-marketing campaigns, and app-level marketing materials. Changes will be reflected across the entire application.
        </AlertDescription>
      </Alert>
    </div>
  );
}
